import {
    Card,
    Text,
    Box,
    BlockStack,
    InlineGrid,
    InlineStack,
    Banner,
    Button,
    RadioButton,
    Checkbox,
    ProgressBar,
    Spinner,
} from '@shopify/polaris';
import { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../utils/api';

/** "1 order" / "412 orders" / "no orders yet", so a zero reads as a fact rather than a blank. */
const countLabel = (n) => (n ? `${n.toLocaleString()} order${n === 1 ? '' : 's'}` : 'No orders yet');

/**
 * The count, plus what the two non-retailer buckets actually hold.
 *
 * "Others" is the one worth explaining here: it holds orders whose tags matched no pattern,
 * which includes OUR orders whose retailer tag prefix is misconfigured. A large count against it
 * is a tag to fix, not a box to tick.
 */
const MARKETPLACE_NOTE = {
    Others: 'tags matched no retailer — includes orders whose tag prefix is misconfigured',
    'Online Store': 'the brand\u2019s own storefront sales',
};
const marketplaceHelp = (name, count) => (
    MARKETPLACE_NOTE[name] ? `${countLabel(count)} \u00b7 ${MARKETPLACE_NOTE[name]}` : countLabel(count)
);

/**
 * Rows that have actually seen orders first, then the rest alphabetically.
 *
 * Both lists on this card need it, for the same reason and to the same rule. The tag list
 * holds every value a tag can resolve to - every retailer configured anywhere in Marketplace
 * Settings, not just the ones this brand sold through - so most rows read "No orders yet". The
 * app list grows the same way on a store with many apps installed. In both, the two or three
 * that matter are somewhere among the rest.
 *
 * Takes the name to sort on, because one list is keyed on a retailer and the other on an app.
 *
 * Deliberately NOT sorted on what is ticked. Sorting by selection would move a row out from
 * under the pointer at the moment it is clicked, which is worse than a little scrolling.
 */
export const byOrdersThenName = (nameOf) => (a, b) => {
    const seen = (x) => ((x.orderCount || 0) > 0 ? 0 : 1);
    return seen(a) - seen(b) || String(nameOf(a)).localeCompare(String(nameOf(b)));
};

/** Two selections holding the same values, whatever order they arrived in. */
const sameSet = (a = [], b = []) => {
    const left = [...(a || [])].sort();
    const right = [...(b || [])].sort();
    return left.length === right.length && left.every((v, i) => v === right[i]);
};

/**
 * Whether this form differs from the brand record it was loaded from.
 *
 * Compares what SAVE WOULD WRITE, not what is on screen: the save clears both lists whenever
 * the mode is not SELECTED, so a brand switched to All Orders with stale ticks still in state
 * is dirty if those ticks are about to be cleared, and is not dirty if there were none.
 *
 * Order-independent, because the two lists are sets. Re-ticking the same retailers in a
 * different order is not a change, and comparing them positionally would enable Save on one.
 */
export const orderSourcesDirty = (brand, { billingMode, appIds, marketplaces }) => {
    if ((brand?.billingMode || '') !== billingMode) return true;
    const willSaveApps = billingMode === 'SELECTED' ? appIds : [];
    const willSaveTags = billingMode === 'SELECTED' ? marketplaces : [];
    return !sameSet(brand?.billingAppIds, willSaveApps)
        || !sameSet(brand?.billingMarketplaces, willSaveTags);
};

/**
 * What is selected, in a line, for the header beside the Show/Hide toggle.
 *
 * This is the only thing that answers "what is this brand billing on" while the pickers are
 * shut, which is most of the time. Once the mode is SELECTED, the picked sources ARE the
 * setting - a collapsed panel with no summary would hide the setting behind a click.
 *
 * "Nothing selected" is a real answer and the most important one: it means no orders are
 * billed at all, while the mode above still reads "Selected Sources Only" and looks
 * configured. Said in words rather than as "0 apps · 0 tags", which reads like a count that
 * happens to be low rather than a brand that is billing nothing.
 *
 * @param {string[]} appIds selected Shopify app ids
 * @param {string[]} marketplaces selected order-tag values
 */
export const describeSelection = (appIds = [], marketplaces = []) => {
    const apps = Array.isArray(appIds) ? appIds.length : 0;
    const tags = Array.isArray(marketplaces) ? marketplaces.length : 0;
    if (apps === 0 && tags === 0) return 'Nothing selected';

    const parts = [];
    if (apps) parts.push(`${apps} app${apps === 1 ? '' : 's'}`);
    if (tags) parts.push(`${tags} tag${tags === 1 ? '' : 's'}`);
    return parts.join(' · ');
};

/**
 * Caps the height of a checkbox list so the card stops growing with the number of retailers.
 *
 * At roughly seven rows the list scrolls instead. Two columns and this cap together are what
 * keep this card the same shape as Brand Status beside it - it was reaching about three times
 * that height, pushing Commission Rates and Invoice Settings off the screen entirely.
 */
const SCROLL_CAP = { maxHeight: '244px', overflowY: 'auto' };

export const OrderSourceSettings = ({
    brand,
    saving,
    setSaving,
    setError,
    fetchBrandData,
}) => {
    /**
     * billingMode as the brand record reports it.
     *
     * Was `brand?.billingMode || 'NOT_CONFIGURED'`. Brand.billingMode is non-nullable
     * `String @default("ALL")` (schema.prisma:67), and NOT_CONFIGURED is a real backend value
     * meaning "bill nothing" (billing-filter-helper.js:14) - so whenever the payload simply
     * omitted the field, this screen asserted the critical banner "No orders will be billed"
     * about a brand that was in fact billing everything. Empty means UNKNOWN, which is neither
     * mode and is stated as such.
     */
    const [billingMode, setBillingMode] = useState(brand?.billingMode || '');
    const [selectedAppIds, setSelectedAppIds] = useState(brand?.billingAppIds || []);
    const [selectedMarketplaces, setSelectedMarketplaces] = useState(brand?.billingMarketplaces || []);
    const [availableApps, setAvailableApps] = useState([]);
    const [availableMarketplaces, setAvailableMarketplaces] = useState([]);
    const [nullAppCount, setNullAppCount] = useState(0);
    const [loadingApps, setLoadingApps] = useState(false);

    // Both pickers and the no-source warning come from one call.
    const loadAppData = useCallback(async () => {
        if (!brand?.id) return;
        setLoadingApps(true);
        try {
            const options = await apiClient.getBillingScopeOptions(brand.id);
            setAvailableApps(options.apps || []);
            setAvailableMarketplaces(options.marketplaces || []);
            setNullAppCount(options.nullAppCount || 0);
        } catch (err) {
            console.error('Failed to load billing scope options:', err);
        } finally {
            setLoadingApps(false);
        }
    }, [brand?.id]);

    useEffect(() => {
        loadAppData();
    }, [loadAppData]);

    // Sync state when brand prop changes
    useEffect(() => {
        setBillingMode(brand?.billingMode || '');
        setSelectedAppIds(brand?.billingAppIds || []);
        setSelectedMarketplaces(brand?.billingMarketplaces || []);
    }, [brand?.billingMode, brand?.billingAppIds, brand?.billingMarketplaces]);

    const toggleIn = (setter) => (value) => setter(prev => (
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    ));
    const handleAppToggle = toggleIn(setSelectedAppIds);
    const handleMarketplaceToggle = toggleIn(setSelectedMarketplaces);

    // Nothing is billed until one of the two lists names something.
    const nothingSelected = selectedAppIds.length === 0 && selectedMarketplaces.length === 0;

    // Save is offered only when there is something to save. Enabled with nothing changed, it
    // invites a write that reports success and alters nothing - and on this card a pointless
    // write is not free: a scope change re-queues the current month's billing report.
    const dirty = orderSourcesDirty(brand, {
        billingMode,
        appIds: selectedAppIds,
        marketplaces: selectedMarketplaces,
    });

    /**
     * Whether the pickers are open.
     *
     * Shut by default, because the lists are the tallest thing on this tab and most visits here
     * are not about changing them. What is selected still has to be READABLE while shut - the
     * summary beside the toggle carries it - because once the mode is SELECTED, which sources
     * are picked IS the setting, and hiding it behind a click hides the answer to "what is this
     * brand billing on".
     *
     * Open when nothing is selected. That is the state where no orders are billed at all, and
     * it is precisely the one a shut panel would conceal: the mode reads "Selected Sources
     * Only", which looks configured, while the lists behind it are empty.
     */
    const [pickersOpen, setPickersOpen] = useState(false);

    // Re-evaluated when the loaded selection changes, not only on mount: the brand's own
    // values arrive after the first render, so a decision taken at mount would be taken
    // against an empty selection every time and always open.
    useEffect(() => {
        if (nothingSelected) setPickersOpen(true);
    }, [nothingSelected]);

    const selectionSummary = describeSelection(selectedAppIds, selectedMarketplaces);

    // Sorted once per payload, not per render and not per click. Both lists, same rule: a
    // store with many apps installed buries its billing app exactly as an estate with many
    // retailers buries the two this brand sells through.
    const sortedMarketplaces = useMemo(
        () => [...availableMarketplaces].sort(byOrdersThenName((m) => m.marketplace)),
        [availableMarketplaces],
    );
    const sortedApps = useMemo(
        () => availableApps
            .filter((app) => app.appId)
            .sort(byOrdersThenName((app) => app.appName || app.appId)),
        [availableApps],
    );

    const saveSettings = async () => {
        try {
            setSaving(true);
            await apiClient.updateBrand(brand.id, {
                billingMode,
                // Cleared together when the mode does not read them, so a stale selection
                // cannot come back into effect by switching to Selected later.
                billingAppIds: billingMode === 'SELECTED' ? selectedAppIds : [],
                billingMarketplaces: billingMode === 'SELECTED' ? selectedMarketplaces : [],
            });
            await fetchBrandData();
        } catch (err) {
            setError(err.message || 'Failed to save order source settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card>
            {/* 200 on top of Card's own 400 = a 24px inset, the value every card in this tab
                now shares. It was 600, giving 40px against Brand Status's 32 beside it — two
                cards in one grid row that were not the same shape. */}
            <Box padding="200">
                <BlockStack gap="300">
                    {/* Save sits in the header, matching Commission Rates below.
                        At the foot of the card it was the last thing in a column of radio
                        buttons and conditional banners, so it moved down the card as those
                        appeared and read as belonging to whichever banner was above it. */}
                    <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
                        <BlockStack gap="200">
                            <Text variant="headingMd" as="h3">Order Sources to Bill</Text>
                            <Text variant="bodyMd" tone="subdued">
                                Choose which order sources should be included in billing for this brand
                            </Text>
                        </BlockStack>
                        <Button
                            variant="primary"
                            onClick={saveSettings}
                            loading={saving}
                            disabled={saving || !dirty}
                        >
                            Save
                        </Button>
                    </InlineStack>

                    {/* Only claim "nothing will be billed" when the server actually said
                        NOT_CONFIGURED. An absent value is unknown, not a billing state. */}
                    {billingMode === 'NOT_CONFIGURED' && (
                        <Banner tone="warning">
                            Order sources not configured. No orders will be billed until you configure this setting.
                        </Banner>
                    )}

                    {billingMode === '' && (
                        <Banner tone="info">
                            The billing mode for this brand has not loaded, so the sources currently being billed are unknown.
                        </Banner>
                    )}

                    {nullAppCount > 0 && billingMode === 'SELECTED' && (
                        <Banner tone="info">
                            {`${nullAppCount} orders have no order source recorded, so no app can bill them. `}
                            {'Pick the retailers they belong to below, or run a full sync to give them a source.'}
                        </Banner>
                    )}

                    <BlockStack gap="300">
                        <RadioButton
                            label="All Orders"
                            helpText="Bill everything from this store (for full-store brands)"
                            checked={billingMode === 'ALL'}
                            id="billing-mode-all"
                            name="billingMode"
                            onChange={() => setBillingMode('ALL')}
                        />
                        {/* The summary sits ON the option it describes. Beside the toggle at the
                            foot of the card it was four rows and a banner away from the words
                            "Selected Sources Only", so the mode and what it actually selects
                            had to be read as two separate facts. */}
                        <RadioButton
                            label={(
                                <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
                                    <Text variant="bodyMd" as="span">Selected Sources Only</Text>
                                    {billingMode === 'SELECTED' && (
                                        <Text
                                            variant="bodySm"
                                            as="span"
                                            tone={nothingSelected ? 'critical' : 'subdued'}
                                        >
                                            {selectionSummary}
                                        </Text>
                                    )}
                                </InlineStack>
                            )}
                            helpText="Bill only orders from specific apps/channels (for marketplace-only brands)"
                            checked={billingMode === 'SELECTED'}
                            id="billing-mode-selected"
                            name="billingMode"
                            onChange={() => setBillingMode('SELECTED')}
                        />
                    </BlockStack>

                    {/* Which months a change here reaches. Scope is applied when a report is
                        built, not frozen on the order like a commission rate, so regenerating
                        an earlier month would move figures the brand has already been billed
                        on. Only the current month is rebuilt; the rest keep what they were
                        billed with until an admin asks for them. Same wording as the tag
                        note below and on Marketplace Patterns, deliberately. */}
                    <Banner tone="info">
                        <Text variant="bodyMd">
                            Applies to this month and future reports. Months already reported keep the
                            figures they were billed with &mdash; to apply these settings to an earlier
                            month, run a billing sync for that month with <b>Force recalculation with
                            current settings</b>.
                        </Text>
                    </Banner>

                    {/* The toggle alone, always present while the mode is SELECTED. What is
                        selected is stated on the radio above, where the setting itself is;
                        this only opens and shuts the lists. */}
                    {billingMode === 'SELECTED' && (
                        <InlineStack>
                            <Button
                                onClick={() => setPickersOpen((open) => !open)}
                                ariaExpanded={pickersOpen}
                                ariaControls="order-source-pickers"
                                disclosure={pickersOpen ? 'up' : 'down'}
                            >
                                {pickersOpen ? 'Hide sources' : 'Show sources'}
                            </Button>
                        </InlineStack>
                    )}

                    {billingMode === 'SELECTED' && pickersOpen && (
                        <div id="order-source-pickers">
                        <Card>
                            {/* Same again: Card supplies its own 16px. */}
                            <Box>
                                <BlockStack gap="400">
                                    {loadingApps ? (
                                        <InlineStack gap="200" blockAlign="center">
                                            <Spinner size="small" />
                                            <Text variant="bodySm" tone="subdued">Loading options...</Text>
                                        </InlineStack>
                                    ) : (
                                        <>
                                            {/* Two lists, and an order needs to match only ONE of them.
                                                Apps answer "we created this order"; retailers answer
                                                "this order reached a retailer", which is the only way to
                                                name an order that came through another company's
                                                connector and so carries a foreign app id. */}
                                            <BlockStack gap="300">
                                                <BlockStack gap="050">
                                                    <Text variant="headingSm" as="h4">Order sources</Text>
                                                    <Text variant="bodySm" tone="subdued">
                                                        Apps that created orders in this store.
                                                    </Text>
                                                </BlockStack>

                                                {availableApps.length > 0 ? (
                                                    // Capped like the tags below, but one column: an app label
                                                    // carries its id — "2w-development (401404887041)" — and two
                                                    // of those side by side in half a card wrap into nonsense.
                                                    <div style={SCROLL_CAP}>
                                                        <BlockStack gap="200">
                                                            {sortedApps.map(app => (
                                                                <Checkbox
                                                                    key={app.appId}
                                                                    label={`${app.appName || 'Unknown App'} (${app.appId})`}
                                                                    helpText={countLabel(app.orderCount)}
                                                                    checked={selectedAppIds.includes(app.appId)}
                                                                    onChange={() => handleAppToggle(app.appId)}
                                                                />
                                                            ))}
                                                        </BlockStack>
                                                    </div>
                                                ) : (
                                                    <Text variant="bodySm" tone="subdued">
                                                        No apps found. Run a sync first to discover available order sources.
                                                    </Text>
                                                )}
                                            </BlockStack>

                                            <BlockStack gap="300">
                                                <BlockStack gap="050">
                                                    {/* "Order Tags", not "Retailers": the list holds every
                                                        value an order's tags can resolve to, and two of them —
                                                        Online Store and Others — are not retailers. */}
                                                    {/* No count here. The summary beside the Show/Hide toggle
                                                        already states it, and states it whether this panel is
                                                        open or shut — which is the case that actually needed
                                                        answering. */}
                                                    <Text variant="headingSm" as="h4">Order Tags</Text>
                                                    {/* The resolution note was a Banner of its own. It is guidance,
                                                        not an alert, and as a banner it cost four lines directly
                                                        above the list it describes — on a card that was already the
                                                        tallest thing on the tab. */}
                                                    <Text variant="bodySm" tone="subdued">
                                                        What each order&rsquo;s tags resolved to. Pick these to bill
                                                        orders that reached a retailer through another connector,
                                                        or to include the brand&rsquo;s own storefront sales. A tag is
                                                        resolved once, when the order is imported &mdash; correcting a
                                                        pattern in Marketplace Settings does not move orders already
                                                        imported.
                                                    </Text>
                                                </BlockStack>

                                                {/* Two columns and a capped height. This card sits in a
                                                    half-width column beside Brand Status, and every retailer
                                                    configured anywhere appears in this list — so it grows with
                                                    the estate rather than with anything about this brand.
                                                    Fourteen rows for the two a brand actually sells through is
                                                    the normal case, not the bad one. */}
                                                <div style={SCROLL_CAP}>
                                                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="200">
                                                        {sortedMarketplaces.map(({ marketplace, orderCount }) => (
                                                            <Checkbox
                                                                key={marketplace}
                                                                label={marketplace}
                                                                helpText={marketplaceHelp(marketplace, orderCount)}
                                                                checked={selectedMarketplaces.includes(marketplace)}
                                                                onChange={() => handleMarketplaceToggle(marketplace)}
                                                            />
                                                        ))}
                                                    </InlineGrid>
                                                </div>
                                            </BlockStack>

                                            {nothingSelected && (
                                                <Banner tone="warning">
                                                    Nothing selected. No orders will be billed for this brand.
                                                </Banner>
                                            )}
                                        </>
                                    )}
                                </BlockStack>
                            </Box>
                        </Card>
                        </div>
                    )}

                    {saving && <ProgressBar progress={undefined} size="small" />}
                </BlockStack>
            </Box>
        </Card>
    );
};
